import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Loader, X, LogIn, ChevronDown, Search, ArrowLeft, Check } from 'lucide-react';
import DoctorSummaryCard from './DoctorSummaryCard';

import { checkEmailAvailability, checkPhoneAvailability, getCountriesFromFirebase } from '../services/FirebaseService';

const BookingForm = ({
  formData,
  setFormData,
  validationErrors,
  setValidationErrors,
  acceptedTerms,
  setAcceptedTerms,
  loading,
  selectedSpecialty,
  selectedDate,
  selectedTime,
  selectedSlot,
  specialtyConfig,
  onSubmit,
  onBack,
  utmParams,
  selectedDoctor
}) => {
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);
  const [showExistingUserModal, setShowExistingUserModal] = useState(false);
  const [existingUserMessage, setExistingUserMessage] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const countryDropdownRef = useRef(null);
  

  const API_URL = 'https://odoo.core.daisies.mx/marketing-manager/import-data';

  // Cargar países al iniciar
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        
        const countriesList = await getCountriesFromFirebase();
        
        setCountries(countriesList);
        setFilteredCountries(countriesList);
        
        // Buscar México en la lista
        const mexico = countriesList.find(c => c.code === 'MX');
        if (mexico) {
          // Inicializar formData con México si no está ya configurado
          if (!formData.phoneCountry || formData.phoneCountry === '') {
            setFormData(prev => ({
              ...prev,
              phoneCountry: mexico.code,
              phoneCode: mexico.phone_code
            }));
          }
        } else {
          if (countriesList.length > 0 && (!formData.phoneCountry || formData.phoneCountry === '')) {
            const firstCountry = countriesList[0];
            setFormData(prev => ({
              ...prev,
              phoneCountry: firstCountry.code,
              phoneCode: firstCountry.phone_code
            }));
          }
        }
      } catch (error) {
        if (!formData.phoneCountry || formData.phoneCountry === '') {
          setFormData(prev => ({
            ...prev,
            phoneCountry: 'MX',
            phoneCode: '+52'
          }));
        }
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [formData.phoneCountry]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && 
          countryDropdownRef.current && 
          !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  // Filtrar países cuando cambia el término de búsqueda
  useEffect(() => {
    if (!countrySearchTerm.trim()) {
      setFilteredCountries(countries);
      return;
    }
    
    const term = countrySearchTerm.toLowerCase().trim();
    const filtered = countries.filter(country => 
      country.name.toLowerCase().includes(term) ||
      country.code.toLowerCase().includes(term) ||
      country.phone_code.includes(term)
    );
    
    setFilteredCountries(filtered);
  }, [countrySearchTerm, countries]);

  // Función para obtener país seleccionado
  const getSelectedCountry = () => {
    const foundCountry = countries.find(c => c.code === formData.phoneCountry);
    
    if (foundCountry) {
      return foundCountry;
    }

    return {
      id: 156,
      code: 'MX',
      name: 'México',
      phone_code: '+52'
    };
  };

  // Función para obtener México (país por defecto)
  const getMexicoCountry = () => {
    return countries.find(c => c.code === 'MX') || {
      code: 'MX',
      name: 'México',
      phone_code: '+52',
      sequence: 1
    };
  };

  // Función para manejar selección de país
  const handleCountrySelect = (country) => {
    setFormData({
      ...formData,
      phoneCountry: country.code,
      phoneCode: country.phone_code
    });
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
    
    // Limpiar teléfono si cambia de país para evitar formatos incorrectos
    setFormData(prev => ({
      ...prev,
      telefono: ''
    }));
  };

  // Función para mostrar modal de usuario existente
  const showExistingUserPopup = (field, value) => {
    const message = field === 'email'
      ? `Este correo electrónico (${value}) ya está registrado. Inicia sesión en la web app para continuar.`
      : `Este número de teléfono (${value}) ya está registrado. Inicia sesión en la web app para continuar.`;
    
    setExistingUserMessage(message);
    setShowExistingUserModal(true);
  };

  const redirectToApp = () => {
    window.location.href = 'https://app.daisies.mx/authPage';
  };
// FUNCIÓN PARA ENVIAR LEAD A LA API - ACTUALIZADA
const sendLeadToAPI = async (leadData) => {
  try {

    const selectedCountry = getSelectedCountry();
    const specConfig = specialtyConfig[selectedSpecialty];

    // Obtener el consultation_reason_id del valor seleccionado
    // const consultationReasonId = parseInt(leadData.consultationReason) || 1;
    
    const payload = {
      params: {
        res_model: "crm.lead",
        res_method: "create_lead_from_campaign",
        res_id: 1,
        res_params: {
          name: leadData.nombre,
          lastname: leadData.apellido,
          email: leadData.email,
          password: leadData.password,
          phone: leadData.telefono,
          country_id: selectedCountry.id,
          country_code: selectedCountry.phone_code,
          pronoun_id: leadData.pronoun ? parseInt(leadData.pronoun, 10) : 1,
          birth_date: leadData.birthDate,
          birth_sex: leadData.birthSex,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Mexico_City",
          utm_campaign: utmParams.campaign || 'DefaultCampaign',
          utm_medium: utmParams.medium || 'Website',
          utm_source: utmParams.source || 'Website',
          // consultation_reason_id: consultationReasonId, // <-- AÑADIR ESTA LÍNEA
          ...(selectedSlot && specConfig && {
            schedule_id: selectedSlot.id,
            professional_ids: selectedSlot.professional_ids 
              ? selectedSlot.professional_ids.map(p => p.id)
              : selectedSlot.professional_ids?.[0]?.id 
                ? [selectedSlot.professional_ids[0].id]
                : [],
            duration_id: selectedSlot?.duration_id || specConfig.duration || 109,
            specialty_id: specConfig.id || 16
          })
        }
      }
    };

    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (data.result?.success) {
      return { 
        success: true, 
        leadId: data.result.lead_id,
        data: data.result.data
      };
    } else {
      return { 
        success: false, 
        error: data.result?.error || data.error?.message || 'Error desconocido en la API' 
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

  // Validación del formulario
  const validateForm = async () => {
    const errors = {};

    // Validar email
    if (!formData.email.includes('@') || formData.email.length > 100) {
      errors.email = 'El correo debe contener @ y tener máximo 100 caracteres';
    } else {
      const isEmailAvailable = await checkEmailAvailability(formData.email);
      if (!isEmailAvailable) {
        showExistingUserPopup('email', formData.email);
        return false;
      }
    }

    // Validar contraseña
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      alert('La contraseña debe tener mínimo 8 caracteres, al menos 1 letra y 1 número');
      return false;
    }

    // Validar teléfono con código de país
    if (!formData.telefono) {
      errors.telefono = 'El teléfono es requerido';
    } else {
      // Validaciones específicas por país
      const cleanPhone = formData.telefono.replace(/[\s\-()]/g, '');
      
      if (!/^\d+$/.test(cleanPhone)) {
        errors.telefono = 'El teléfono debe contener solo números';
      } else if (formData.phoneCountry === 'MX') {
        // Para México: 10 dígitos
        if (cleanPhone.length !== 10) {
          errors.telefono = 'Para México, el teléfono debe tener 10 dígitos';
        }
      } else if (formData.phoneCountry === 'US') {
        // Para Estados Unidos: 10 dígitos
        if (cleanPhone.length !== 10) {
          errors.telefono = 'Para Estados Unidos, el teléfono debe tener 10 dígitos';
        }
      } else {
        // Para otros países: entre 5 y 15 dígitos
        if (cleanPhone.length < 5) {
          errors.telefono = 'El número de teléfono es demasiado corto';
        }
        if (cleanPhone.length > 15) {
          errors.telefono = 'El número de teléfono es demasiado largo';
        }
      }
      
      if (!errors.telefono) {
        // Combinar código de país con número para la validación en Firebase
        const fullPhoneNumber = formData.phoneCode + cleanPhone;
        const isPhoneAvailable = await checkPhoneAvailability(fullPhoneNumber);
        if (!isPhoneAvailable) {
          showExistingUserPopup('telefono', `${formData.phoneCode}${cleanPhone}`);
          return false;
        }
      }
    }

    // Validar nombre
    if (!formData.nombre.trim()) {
      alert('Por favor ingresa tu nombre');
      return false;
    }

    // Validar apellido
    if (!formData.apellido.trim()) {
      alert('Por favor ingresa tu apellido');
      return false;
    }

    // Validar fecha de nacimiento
    if (!formData.birthDate) {
      alert('Por favor ingresa tu fecha de nacimiento');
      return false;
    }

    const birthDate = new Date(formData.birthDate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const hasBirthdayPassed = monthDiff > 0 || (monthDiff === 0 && today.getDate() >= birthDate.getDate());
    const actualAge = hasBirthdayPassed ? age : age - 1;

    if (birthDate >= today) {
      alert('La fecha de nacimiento debe ser menor a la fecha actual');
      return false;
    }

    if (actualAge < 18) {
      alert('Debes ser mayor de 18 años para continuar');
      return false;
    }

    // Validar sexo de nacimiento
    if (!formData.birthSex) {
      alert('Por favor selecciona tu sexo de nacimiento');
      return false;
    }

    // Validar pronombre
    if (!formData.pronoun) {
      alert('Por favor selecciona tu pronombre');
      return false;
    }

    // Validar términos
    if (!acceptedTerms) {
      alert('Debes aceptar los términos y condiciones para continuar');
      return false;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que tenemos los datos necesarios
    if (!selectedSpecialty || !selectedDate || !selectedTime) {
      alert('Por favor completa todos los datos de la cita primero');
      return;
    }

    // Clear previous validation errors
    setValidationErrors({ email: '', telefono: '' });

    // Validar formulario
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setSendingLead(true);

    try {
      // Enviar el lead a la API
      const apiResult = await sendLeadToAPI(formData);
      
      if (apiResult.success) {
        
        // Llamar al onSubmit del padre con TODOS los datos
        onSubmit({
          success: true,
          leadId: apiResult.leadId,
          email: formData.email,
          password: formData.password,
          nombre: formData.nombre,
          apellido: formData.apellido,
          telefono: formData.telefono,
          phoneCountry: formData.phoneCountry,
          phoneCode: formData.phoneCode,
          birthDate: formData.birthDate,
          birthSex: formData.birthSex,
          pronoun: formData.pronoun,
          consultationReason: formData.consultationReason, // AÑADIDO
          apiData: apiResult.data
        });
        
      } else {
        alert(`Hubo un error al guardar tus datos: ${apiResult.error}. Por favor intenta de nuevo.`);
      }
    } catch (error) {
      alert('Hubo un error al procesar tu solicitud. Por favor intenta de nuevo.');
    } finally {
      setSendingLead(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const isSubmitting = loading || sendingLead;

  return (
    <>
      {/* Modal para usuario existente */}
      {showExistingUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-elevated max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-brand-tint rounded-full flex items-center justify-center">
                    <LogIn className="text-brand" size={20} />
                  </div>
                  <h3 className="text-lg font-bold text-text-primary">
                    Usuario existente
                  </h3>
                </div>
                <button
                  onClick={() => setShowExistingUserModal(false)}
                  className="text-text-mute hover:text-text-strong"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-text-body mb-4">
                  {existingUserMessage}
                </p>
                <div className="bg-brand-tint rounded-xl p-4">
                  <p className="text-sm text-brand-deep font-medium mb-2">
                    Inicia sesión para continuar
                  </p>
                  <p className="text-sm text-brand-deep">
                    Accede a tu cuenta existente para ver tus resultados o completar el test.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={redirectToApp}
                  className="press-scale w-full px-4 py-3 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all flex items-center justify-center gap-2"
                >
                  <LogIn size={20} />
                  Ir a iniciar sesión
                </button>
                <button
                  onClick={() => setShowExistingUserModal(false)}
                  className="w-full px-4 py-3 border-[1.5px] border-brand text-daisies rounded-full font-semibold hover:bg-daisies/5 transition-colors"
                >
                  Volver al formulario
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-6">
        <button 
          onClick={onBack} 
          className="press-scale mb-6 text-brand flex items-center gap-2 hover:gap-3 transition-all text-sm font-semibold"
        >
          <ArrowLeft size={20} aria-hidden="true" /> Volver
        </button>

        <div className="bg-surface-card p-6 rounded-card">
          <h2 className="text-2xl font-bold text-text-primary mb-1">Completa tus datos</h2>
          <p className="text-text-body mb-6 text-sm">Necesitamos esta información para crear tu perfil</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nombre y Apellido */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-text-strong mb-1">Nombre *</label>
                <input 
                  type="text" 
                  name="nombre"
                  required
                  value={formData.nombre}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  placeholder="María"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-strong mb-1">Apellido *</label>
                <input 
                  type="text" 
                  name="apellido"
                  required
                  value={formData.apellido}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  placeholder="González"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-text-strong mb-1">Correo electrónico *</label>
              <input 
                type="email" 
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 text-sm bg-warm-canvas rounded-xl focus:outline-none transition-colors ${
                  validationErrors.email
                    ? 'ring-2 ring-red-400'
                    : 'focus:ring-2 focus:ring-brand'
                }`}
                placeholder="tu@email.com"
                maxLength={100}
                disabled={isSubmitting}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
              )}
            </div>

            {/* Contraseña */}
            <div>
              <label className="block text-xs font-semibold text-text-strong mb-1">Contraseña *</label>
              <input 
                type="password" 
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                placeholder="Mín. 8 caracteres, 1 letra y 1 número"
                minLength={8}
                disabled={isSubmitting}
              />
            </div>

            {/* Razón de consulta */}
{/* 
            <div>
              <label className="block text-xs font-semibold text-text-strong mb-1">Motivo de consulta *</label>
              <div className="relative">
                <select
                  name="consultationReason"
                  required
                  value={formData.consultationReason}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors appearance-none bg-white"
                  disabled={isSubmitting}
                >
                  <option value="">Selecciona un motivo...</option>
                  <option value="1">Ansiedad y estrés crónico</option>
                  <option value="2">Dificultad para manejar emociones y reacciones impulsivas</option>
                  <option value="3">Problemas de autoestima y confianza</option>
                  <option value="4">Duelos y procesos de pérdida</option>
                  <option value="5">Dificultades en relaciones de pareja o familiares</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </div>
              </div>
            </div>
*/}

            {/* Teléfono con selector de país */}
            <div className="space-y-1">
              <label className="block text-xs font-semibold text-text-strong mb-1">Teléfono *</label>
              
              <div className="flex">
                {/* Selector de país */}
                <div className="relative flex-shrink-0" ref={countryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className={`flex items-center justify-between w-28 px-3 py-2 rounded-l-xl bg-warm-canvas transition-colors focus:outline-none focus:ring-2 focus:ring-brand ${
                      validationErrors.telefono ? 'ring-2 ring-red-400' : ''
                    }`}
                    disabled={loadingCountries || isSubmitting}
                  >
                    {loadingCountries ? (
                      <Loader className="animate-spin text-daisies w-4 h-4" />
                    ) : (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-daisies text-sm">
                            {getSelectedCountry().phone_code}
                          </span>
                          {getSelectedCountry().code === 'MX' && (
                            <span className="text-xs bg-mint-pill text-mint-deep px-1.5 py-0.5 rounded">
                              MX
                            </span>
                          )}
                        </div>
                        <ChevronDown className={`w-3 h-3 text-text-mute transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </button>

                  {/* Dropdown de países */}
                  {showCountryDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-elevated z-50 max-h-80 overflow-hidden">
                      {/* Barra de búsqueda */}
                      <div className="p-2 border-b border-brand-100 bg-warm-canvas">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-text-mute" />
                          <input
                            type="text"
                            placeholder="Buscar país..."
                            value={countrySearchTerm}
                            onChange={(e) => setCountrySearchTerm(e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 text-sm bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                            autoFocus
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>

                      {/* Lista de países */}
                      <div className="overflow-y-auto max-h-48">
                        {filteredCountries.length === 0 ? (
                          <div className="p-3 text-center text-text-mute text-sm">
                            No se encontraron países con "{countrySearchTerm}"
                          </div>
                        ) : (
                          <div className="divide-y divide-brand-100">
                            {/* México destacado al principio si está en los resultados filtrados */}
                            {filteredCountries.find(c => c.code === 'MX') && (
                              <button
                                type="button"
                                onClick={() => handleCountrySelect(getMexicoCountry())}
                                className="w-full px-3 py-2.5 text-left hover:bg-mint-pill/40 transition-colors flex items-center justify-between bg-mint-pill/40 border-b border-brand-100 text-sm"
                                disabled={isSubmitting}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-text-primary">
                                    {getMexicoCountry().name}
                                  </span>
                                  <span className="text-xs bg-mint-deep text-white px-1.5 py-0.5 rounded">
                                    {getMexicoCountry().code}
                                  </span>
                                </div>
                                <span className="font-bold text-mint-deep">
                                  {getMexicoCountry().phone_code}
                                </span>
                              </button>
                            )}
                            
                            {/* Resto de países */}
                            {filteredCountries
                              .filter(country => country.code !== 'MX') // Excluir México si ya se mostró
                              .map(country => (
                                <button
                                  key={country.id || country.code}
                                  type="button"
                                  onClick={() => handleCountrySelect(country)}
                                  className={`w-full px-3 py-2.5 text-left hover:bg-brand-tint transition-colors flex items-center justify-between text-sm ${
                                    formData.phoneCountry === country.code ? 'bg-brand-tint' : ''
                                  }`}
                                  disabled={isSubmitting}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-text-primary">
                                      {country.name}
                                    </span>
                                    <span className="text-xs text-text-mute">
                                      ({country.code})
                                    </span>
                                  </div>
                                  <span className="font-semibold text-daisies">
                                    {country.phone_code}
                                  </span>
                                </button>
                              ))
                            }
                          </div>
                        )}
                      </div>

                      {/* Footer con info del país seleccionado */}
                      <div className="p-2 border-t border-brand-100 bg-warm-canvas">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-text-body">Seleccionado:</span>
                          <div className="flex items-center gap-1">
                            <span className="font-bold text-daisies">
                              {getSelectedCountry().phone_code}
                            </span>
                            <span className="font-semibold text-text-primary">
                              {getSelectedCountry().name}
                            </span>
                            {getSelectedCountry().code === 'MX' && (
                              <span className="text-xs bg-daisies text-white px-1.5 py-0.5 rounded">
                                Predeterminado
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Campo de número de teléfono */}
                <input
                  type="tel"
                  name="telefono"
                  required
                  placeholder={formData.phoneCountry === 'MX' ? "10 dígitos" : "Número"}
                  value={formData.telefono}
                  onChange={(e) => {
                    // Solo permitir números
                    const value = e.target.value.replace(/[^\d]/g, '');
                    // Limitar según el país
                    const maxLength = formData.phoneCountry === 'MX' ? 10 : 15;
                    setFormData({ 
                      ...formData, 
                      telefono: value.slice(0, maxLength) 
                    });
                    if (validationErrors.telefono) {
                      setValidationErrors({...validationErrors, telefono: ''});
                    }
                  }}
                  className={`flex-1 px-3 py-2 text-sm bg-warm-canvas rounded-r-xl focus:outline-none transition-colors ${
                    validationErrors.telefono
                      ? 'ring-2 ring-red-400'
                      : 'focus:ring-2 focus:ring-brand'
                  }`}
                  maxLength={formData.phoneCountry === 'MX' ? 10 : 15}
                  disabled={isSubmitting}
                />
              </div>
              
              {/* Información de ayuda */}
              <div className="flex flex-col gap-0.5 text-xs text-text-mute">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">País: </span>
                    <span className="font-semibold">{getSelectedCountry().name}</span>
                    {getSelectedCountry().code === 'MX' && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-mint-deep text-xs"><Check size={11} aria-hidden="true" /> Predeterminado</span>
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Formato: </span>
                    <span className="font-semibold">
                      {formData.phoneCode}{formData.telefono || '__________'}
                    </span>
                  </div>
                </div>
                {validationErrors.telefono && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.telefono}</p>
                )}
                {formData.phoneCountry === 'MX' && formData.telefono.length !== 10 && formData.telefono.length > 0 && (
                  <div className="text-amber-deep font-medium text-xs mt-0.5">
                    Para México, el número debe tener exactamente 10 dígitos
                  </div>
                )}
              </div>
            </div>

            {/* Fecha de nacimiento */}
            <div>
              <label className="block text-xs font-semibold text-text-strong mb-1">Fecha de nacimiento *</label>
              <input 
                type="date" 
                name="birthDate"
                required
                value={formData.birthDate}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                max={new Date().toISOString().split('T')[0]}
                disabled={isSubmitting}
              />
            </div>
            <p className="text-xs text-text-mute -mt-2">Debes ser mayor de 18 años</p>

            {/* Sexo de nacimiento y Pronombre */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-text-strong mb-1">Sexo de nacimiento *</label>
                <select 
                  name="birthSex"
                  required
                  value={formData.birthSex}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  disabled={isSubmitting}
                >
                  <option value="">Selecciona...</option>
                  <option value="female">Femenino</option>
                  <option value="male">Masculino</option>
                  <option value="intersex">Intersexual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-text-strong mb-1">Pronombre *</label>
                <select 
                  name="pronoun"
                  required
                  value={formData.pronoun}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm bg-warm-canvas rounded-input focus:ring-2 focus:ring-brand focus:outline-none transition-colors"
                  disabled={isSubmitting}
                >
                  <option value="">Selecciona...</option>
                  <option value="1">Ella</option>
                  <option value="2">Él</option>
                  <option value="3">Elle</option>
                </select>
              </div>
            </div>

            {/* Términos y condiciones */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 text-daisies focus:ring-daisies"
                disabled={isSubmitting}
              />
              <label htmlFor="terms" className="text-xs text-text-body">
                Acepto los{' '}
                <a 
                  href="https://daisies.mx/terminos-y-condiciones" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-daisies hover:text-daisies-hover underline font-semibold"
                >
                  términos y condiciones
                </a>
              </label>
            </div>

            {/* Resumen de cita */}
            <div className="bg-brand-tint p-3 rounded-xl">
              <label className="block text-xs font-semibold text-text-strong mb-2">Resumen de tu cita</label>
              
              {/* Doctor Summary Card */}
              {selectedDoctor && (
                <div className="mb-3">
                  <DoctorSummaryCard doctor={selectedDoctor} />
                </div>
              )}

              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Especialidad:</span>
                  <span className="font-semibold">{specialtyConfig[selectedSpecialty]?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fecha:</span>
                  <span className="font-semibold">
                    {selectedDate?.getDate()} de {selectedDate?.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Horario:</span>
                  <span className="font-semibold">{selectedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span>Teléfono:</span>
                  <span className="font-semibold">{formData.phoneCode}{formData.telefono}</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 bg-daisies text-white rounded-full text-base font-semibold hover:bg-daisies-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  {sendingLead ? 'Enviando datos...' : 'Procesando...'}
                </>
              ) : (
                <>
                  Confirmar Pre-Reserva
                  <ChevronRight size={20} />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default BookingForm;